import React, { useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Loader2, Users, Building2, CalendarRange, RotateCcw, Briefcase, Search, Layers, X, Plus, CircleDollarSign, Send, ChevronRight, Save, Trash2, Check } from 'lucide-react';
import { companiesApi, fondationsApi, exportsApi, caissesApi, exportPresetsApi } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';

type EntityType = 'customers' | 'pension-funds';

const stateLabels: Record<number, string> = {
  0: 'Nouveau',
  1: 'Contrôle administratif',
  2: 'Soumission institutions',
  3: 'Attente réponses',
  4: 'Dossier clôturé',
};

const stateColors: Record<number, { bg: string; text: string; activeBg: string }> = {
  0: { bg: 'bg-blue-50 border-blue-200', text: 'text-blue-700', activeBg: 'bg-blue-600' },
  1: { bg: 'bg-amber-50 border-amber-200', text: 'text-amber-700', activeBg: 'bg-amber-500' },
  2: { bg: 'bg-purple-50 border-purple-200', text: 'text-purple-700', activeBg: 'bg-purple-600' },
  3: { bg: 'bg-orange-50 border-orange-200', text: 'text-orange-700', activeBg: 'bg-orange-500' },
  4: { bg: 'bg-green-50 border-green-200', text: 'text-green-700', activeBg: 'bg-green-600' },
};

const pensionFundStatuses = ['Nouveau', 'En cours', 'Transféré', 'Terminé', 'Annulé', 'Rejeté'];

const statusColors: Record<string, { bg: string; text: string; activeBg: string }> = {
  'Nouveau': { bg: 'bg-blue-50 border-blue-200', text: 'text-blue-700', activeBg: 'bg-blue-600' },
  'En cours': { bg: 'bg-amber-50 border-amber-200', text: 'text-amber-700', activeBg: 'bg-amber-500' },
  'Transféré': { bg: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-700', activeBg: 'bg-emerald-600' },
  'Terminé': { bg: 'bg-green-50 border-green-200', text: 'text-green-700', activeBg: 'bg-green-600' },
  'Annulé': { bg: 'bg-gray-100 border-gray-200', text: 'text-gray-600', activeBg: 'bg-gray-500' },
  'Rejeté': { bg: 'bg-red-50 border-red-200', text: 'text-red-700', activeBg: 'bg-red-500' },
};

// Column definitions
const customerColumns: Record<string, string> = {
  nom: 'Nom',
  prenom: 'Prénom',
  login: 'Email',
  avs: 'AVS',
  tel: 'Téléphone',
  adr: 'Adresse',
  npa: 'NPA',
  loc: 'Ville',
  pays: 'Pays',
  civil: 'État civil',
  sit_pro: 'Situation pro',
  fondation: 'Fondation',
  state: 'État',
  createdAt: 'Date inscription',
  dat: 'Date naissance',
  company_name: 'Entreprise',
  recherche: 'Recherche',
  regroupement: 'Regroupement',
  total_amount: 'Montant total en cours',
};

const pensionFundColumns: Record<string, string> = {
  client_name: 'Client',
  company_name: 'Entreprise',
  fund_name: 'Caisse de pension',
  fondation: 'Fondation',
  status: 'Statut',
  amount: 'Montant',
  commission_amount: 'Commission',
  commissioned: 'Commissionnée',
  sending_date: 'Date envoi instruction',
  reception_date: 'Date réception',
  commission_date: 'Date commission',
  comments: 'Commentaires',
  created_at: 'Créé le',
};

interface FilterDef {
  key: string;
  label: string;
  icon: React.ReactNode;
  description: string;
}

const customerFilters: FilterDef[] = [
  { key: 'state', label: 'État', icon: <Layers className="w-4 h-4" />, description: 'Filtrer par état du dossier' },
  { key: 'created', label: "Date d'inscription", icon: <CalendarRange className="w-4 h-4" />, description: "Période d'inscription" },
  { key: 'birth', label: 'Date de naissance', icon: <CalendarRange className="w-4 h-4" />, description: 'Plage de dates de naissance' },
  { key: 'company', label: 'Entreprise', icon: <Briefcase className="w-4 h-4" />, description: 'Filtrer par entreprise' },
  { key: 'fondation', label: 'Fondation', icon: <Layers className="w-4 h-4" />, description: 'Filtrer par fondation' },
  { key: 'caisse', label: 'Caisse de pension', icon: <Building2 className="w-4 h-4" />, description: 'Clients ayant une caisse spécifique' },
  { key: 'sit_pro', label: 'Situation pro', icon: <Briefcase className="w-4 h-4" />, description: 'Situation professionnelle' },
  { key: 'recherche', label: 'Recherche', icon: <Search className="w-4 h-4" />, description: 'Clients en recherche' },
  { key: 'regroupement', label: 'Regroupement', icon: <Layers className="w-4 h-4" />, description: 'Clients avec regroupement' },
];

const pensionFundFilters: FilterDef[] = [
  { key: 'status', label: 'Statut', icon: <Layers className="w-4 h-4" />, description: 'Filtrer par statut' },
  { key: 'pf_company', label: 'Entreprise', icon: <Building2 className="w-4 h-4" />, description: 'Filtrer par entreprise' },
  { key: 'amount', label: 'Montant', icon: <CircleDollarSign className="w-4 h-4" />, description: 'Plage de montants (CHF)' },
  { key: 'fondation', label: 'Fondation', icon: <Layers className="w-4 h-4" />, description: 'Filtrer par fondation' },
  { key: 'sending_date', label: 'Date envoi instruction', icon: <Send className="w-4 h-4" />, description: "Période d'envoi" },
  { key: 'reception_date', label: 'Date réception', icon: <CalendarRange className="w-4 h-4" />, description: 'Période de réception' },
  { key: 'commission_date', label: 'Date commission', icon: <CalendarRange className="w-4 h-4" />, description: 'Période de commission' },
  { key: 'commissioned', label: 'Commissionnée', icon: <CircleDollarSign className="w-4 h-4" />, description: 'État de commissionnement' },
];

const ExportPage: React.FC = () => {
  const notify = useNotification();
  const queryClient = useQueryClient();
  const [entityType, setEntityType] = useState<EntityType>('customers');
  const [isExporting, setIsExporting] = useState(false);
  const [activeFilters, setActiveFilters] = useState<Set<string>>(new Set());

  // Column picker modal
  const [showColumnModal, setShowColumnModal] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState<Set<string>>(new Set(Object.keys(customerColumns)));

  // Preset save
  const [showSavePreset, setShowSavePreset] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [isSavingPreset, setIsSavingPreset] = useState(false);

  // Customer filters
  const [selectedStates, setSelectedStates] = useState<number[]>([]);
  const [companyId, setCompanyId] = useState('');
  const [fondation, setFondation] = useState('');
  const [sitPro, setSitPro] = useState('');
  const [recherche, setRecherche] = useState('');
  const [regroupement, setRegroupement] = useState('');
  const [createdFrom, setCreatedFrom] = useState('');
  const [createdTo, setCreatedTo] = useState('');
  const [birthFrom, setBirthFrom] = useState('');
  const [birthTo, setBirthTo] = useState('');
  const [caisse, setCaisse] = useState('');

  // Pension fund filters
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [pfCompanyId, setPfCompanyId] = useState('');
  const [pfFondation, setPfFondation] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [commissioned, setCommissioned] = useState('');
  const [sendingDateFrom, setSendingDateFrom] = useState('');
  const [sendingDateTo, setSendingDateTo] = useState('');
  const [receptionDateFrom, setReceptionDateFrom] = useState('');
  const [receptionDateTo, setReceptionDateTo] = useState('');
  const [commissionDateFrom, setCommissionDateFrom] = useState('');
  const [commissionDateTo, setCommissionDateTo] = useState('');

  const { data: companies } = useQuery({
    queryKey: ['companies'],
    queryFn: async () => (await companiesApi.getAll({ all: true })).data,
  });

  const { data: fondations } = useQuery({
    queryKey: ['fondations'],
    queryFn: async () => (await fondationsApi.getAll()).data,
  });

  const { data: caisses } = useQuery({
    queryKey: ['caisses-list'],
    queryFn: async () => (await caissesApi.getList()).data,
  });

  const { data: presets } = useQuery({
    queryKey: ['export-presets', entityType],
    queryFn: async () => (await exportPresetsApi.list(entityType)).data,
  });

  const currentColumns = entityType === 'customers' ? customerColumns : pensionFundColumns;

  const buildFilters = useCallback((): Record<string, unknown> => {
    if (entityType === 'customers') {
      const filters: Record<string, unknown> = {};
      if (selectedStates.length) filters.state = selectedStates;
      if (companyId) filters.company_id = parseInt(companyId);
      if (fondation) filters.fondation = fondation;
      if (sitPro) filters.sit_pro = sitPro;
      if (caisse) filters.caisse = caisse;
      if (recherche !== '') filters.recherche = recherche === '1' ? 1 : 0;
      if (regroupement !== '') filters.regroupement = regroupement === '1' ? 1 : 0;
      if (createdFrom) filters.created_from = createdFrom;
      if (createdTo) filters.created_to = createdTo;
      if (birthFrom) filters.birth_from = birthFrom;
      if (birthTo) filters.birth_to = birthTo;
      return filters;
    } else {
      const filters: Record<string, unknown> = {};
      if (selectedStatuses.length) filters.status = selectedStatuses;
      if (pfCompanyId) filters.company_id = parseInt(pfCompanyId);
      if (pfFondation) filters.fondation = pfFondation;
      if (minAmount) filters.min_amount = parseFloat(minAmount);
      if (maxAmount) filters.max_amount = parseFloat(maxAmount);
      if (commissioned !== '') filters.commissioned = commissioned === '1' ? 1 : 0;
      if (sendingDateFrom) filters.sending_date_from = sendingDateFrom;
      if (sendingDateTo) filters.sending_date_to = sendingDateTo;
      if (receptionDateFrom) filters.reception_date_from = receptionDateFrom;
      if (receptionDateTo) filters.reception_date_to = receptionDateTo;
      if (commissionDateFrom) filters.commission_date_from = commissionDateFrom;
      if (commissionDateTo) filters.commission_date_to = commissionDateTo;
      return filters;
    }
  }, [entityType, selectedStates, companyId, fondation, sitPro, caisse, recherche, regroupement, createdFrom, createdTo, birthFrom, birthTo, selectedStatuses, pfCompanyId, pfFondation, minAmount, maxAmount, commissioned, sendingDateFrom, sendingDateTo, receptionDateFrom, receptionDateTo, commissionDateFrom, commissionDateTo]);

  const toggleFilter = (key: string) => {
    setActiveFilters(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
        clearFilterValues(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const removeFilter = (key: string) => {
    setActiveFilters(prev => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
    clearFilterValues(key);
  };

  const clearFilterValues = (key: string) => {
    switch (key) {
      case 'state': setSelectedStates([]); break;
      case 'created': setCreatedFrom(''); setCreatedTo(''); break;
      case 'birth': setBirthFrom(''); setBirthTo(''); break;
      case 'company': setCompanyId(''); break;
      case 'fondation': entityType === 'customers' ? setFondation('') : setPfFondation(''); break;
      case 'caisse': setCaisse(''); break;
      case 'sit_pro': setSitPro(''); break;
      case 'recherche': setRecherche(''); break;
      case 'regroupement': setRegroupement(''); break;
      case 'pf_company': setPfCompanyId(''); break;
      case 'status': setSelectedStatuses([]); break;
      case 'amount': setMinAmount(''); setMaxAmount(''); break;
      case 'sending_date': setSendingDateFrom(''); setSendingDateTo(''); break;
      case 'reception_date': setReceptionDateFrom(''); setReceptionDateTo(''); break;
      case 'commission_date': setCommissionDateFrom(''); setCommissionDateTo(''); break;
      case 'commissioned': setCommissioned(''); break;
    }
  };

  const toggleState = (state: number) => {
    setSelectedStates(prev => prev.includes(state) ? prev.filter(s => s !== state) : [...prev, state]);
  };

  const toggleStatus = (status: string) => {
    setSelectedStatuses(prev => prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]);
  };

  const openColumnModal = () => {
    // Reset columns to all selected
    setSelectedColumns(new Set(Object.keys(currentColumns)));
    setShowColumnModal(true);
  };

  const toggleColumn = (key: string) => {
    setSelectedColumns(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        if (next.size > 1) next.delete(key); // keep at least 1
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const toggleAllColumns = () => {
    const allKeys = Object.keys(currentColumns);
    if (selectedColumns.size === allKeys.length) {
      // Deselect all except first
      setSelectedColumns(new Set([allKeys[0]]));
    } else {
      setSelectedColumns(new Set(allKeys));
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    setShowColumnModal(false);
    try {
      const filters = buildFilters();
      const cols = Array.from(selectedColumns);
      const allKeys = Object.keys(currentColumns);
      // Only send columns if not all selected
      const data: Record<string, unknown> = { ...filters };
      if (cols.length < allKeys.length) {
        data.columns = cols;
      }

      let response;
      let filename: string;

      if (entityType === 'customers') {
        response = await exportsApi.exportCustomers(data);
        filename = `clients_export_${new Date().toISOString().slice(0, 10)}.xlsx`;
      } else {
        response = await exportsApi.exportPensionFunds(data);
        filename = `caisses_pension_export_${new Date().toISOString().slice(0, 10)}.xlsx`;
      }

      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      notify.success('Export téléchargé avec succès');
    } catch {
      notify.error("Erreur lors de l'export");
    } finally {
      setIsExporting(false);
    }
  };

  const resetAll = () => {
    setActiveFilters(new Set());
    setSelectedStates([]); setCompanyId(''); setFondation(''); setSitPro('');
    setRecherche(''); setRegroupement(''); setCreatedFrom(''); setCreatedTo('');
    setBirthFrom(''); setBirthTo(''); setSelectedStatuses([]); setPfCompanyId(''); setPfFondation('');
    setMinAmount(''); setMaxAmount(''); setCommissioned(''); setSendingDateFrom('');
    setSendingDateTo(''); setReceptionDateFrom(''); setReceptionDateTo('');
    setCommissionDateFrom(''); setCommissionDateTo(''); setCaisse('');
  };

  const handleEntityChange = (type: EntityType) => {
    setEntityType(type);
    resetAll();
    setSelectedColumns(new Set(Object.keys(type === 'customers' ? customerColumns : pensionFundColumns)));
  };

  // Preset: load
  const loadPreset = (preset: { filters: Record<string, unknown>; columns: string[] | null; entity_type: string }) => {
    const f = preset.filters;
    resetAll();

    const newActive = new Set<string>();

    if (preset.entity_type === 'customers') {
      if (f.state) { setSelectedStates(f.state as number[]); newActive.add('state'); }
      if (f.company_id) { setCompanyId(String(f.company_id)); newActive.add('company'); }
      if (f.fondation) { setFondation(f.fondation as string); newActive.add('fondation'); }
      if (f.sit_pro) { setSitPro(f.sit_pro as string); newActive.add('sit_pro'); }
      if (f.caisse) { setCaisse(f.caisse as string); newActive.add('caisse'); }
      if (f.recherche !== undefined && f.recherche !== '') { setRecherche(String(f.recherche)); newActive.add('recherche'); }
      if (f.regroupement !== undefined && f.regroupement !== '') { setRegroupement(String(f.regroupement)); newActive.add('regroupement'); }
      if (f.created_from) { setCreatedFrom(f.created_from as string); newActive.add('created'); }
      if (f.created_to) { setCreatedTo(f.created_to as string); newActive.add('created'); }
      if (f.birth_from) { setBirthFrom(f.birth_from as string); newActive.add('birth'); }
      if (f.birth_to) { setBirthTo(f.birth_to as string); newActive.add('birth'); }
    } else {
      if (f.status) { setSelectedStatuses(f.status as string[]); newActive.add('status'); }
      if (f.company_id) { setPfCompanyId(String(f.company_id)); newActive.add('pf_company'); }
      if (f.fondation) { setPfFondation(f.fondation as string); newActive.add('fondation'); }
      if (f.min_amount) { setMinAmount(String(f.min_amount)); newActive.add('amount'); }
      if (f.max_amount) { setMaxAmount(String(f.max_amount)); newActive.add('amount'); }
      if (f.commissioned !== undefined && f.commissioned !== '') { setCommissioned(String(f.commissioned)); newActive.add('commissioned'); }
      if (f.sending_date_from) { setSendingDateFrom(f.sending_date_from as string); newActive.add('sending_date'); }
      if (f.sending_date_to) { setSendingDateTo(f.sending_date_to as string); newActive.add('sending_date'); }
      if (f.reception_date_from) { setReceptionDateFrom(f.reception_date_from as string); newActive.add('reception_date'); }
      if (f.reception_date_to) { setReceptionDateTo(f.reception_date_to as string); newActive.add('reception_date'); }
      if (f.commission_date_from) { setCommissionDateFrom(f.commission_date_from as string); newActive.add('commission_date'); }
      if (f.commission_date_to) { setCommissionDateTo(f.commission_date_to as string); newActive.add('commission_date'); }
    }

    setActiveFilters(newActive);

    if (preset.columns) {
      setSelectedColumns(new Set(preset.columns));
    } else {
      setSelectedColumns(new Set(Object.keys(currentColumns)));
    }

    notify.success('Preset chargé');
  };

  // Preset: save
  const savePreset = async () => {
    if (!presetName.trim()) return;
    setIsSavingPreset(true);
    try {
      const filters = buildFilters();
      const cols = Array.from(selectedColumns);
      const allKeys = Object.keys(currentColumns);
      await exportPresetsApi.save({
        name: presetName.trim(),
        entity_type: entityType,
        filters,
        columns: cols.length < allKeys.length ? cols : null,
      });
      queryClient.invalidateQueries({ queryKey: ['export-presets'] });
      setPresetName('');
      setShowSavePreset(false);
      notify.success('Preset sauvegardé');
    } catch {
      notify.error('Erreur lors de la sauvegarde');
    } finally {
      setIsSavingPreset(false);
    }
  };

  // Preset: delete
  const deletePreset = async (id: number) => {
    try {
      await exportPresetsApi.delete(id);
      queryClient.invalidateQueries({ queryKey: ['export-presets'] });
      notify.success('Preset supprimé');
    } catch {
      notify.error('Erreur lors de la suppression');
    }
  };

  const currentFilters = entityType === 'customers' ? customerFilters : pensionFundFilters;
  const inactiveFilters = currentFilters.filter(f => !activeFilters.has(f.key));

  const inputClasses = 'w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-sm';

  const renderFilterContent = (key: string) => {
    switch (key) {
      case 'state':
        return (
          <div className="flex flex-wrap gap-2">
            {Object.entries(stateLabels).map(([k, label]) => {
              const state = parseInt(k);
              const selected = selectedStates.includes(state);
              const colors = stateColors[state];
              return (
                <button key={state} type="button" onClick={() => toggleState(state)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all border ${
                    selected ? `${colors.activeBg} text-white border-transparent` : `${colors.bg} ${colors.text}`
                  }`}
                >{label}</button>
              );
            })}
          </div>
        );

      case 'status':
        return (
          <div className="flex flex-wrap gap-2">
            {pensionFundStatuses.map(status => {
              const selected = selectedStatuses.includes(status);
              const colors = statusColors[status];
              return (
                <button key={status} type="button" onClick={() => toggleStatus(status)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all border ${
                    selected ? `${colors.activeBg} text-white border-transparent` : `${colors.bg} ${colors.text}`
                  }`}
                >{status}</button>
              );
            })}
          </div>
        );

      case 'created':
        return (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Du</label>
              <input type="date" value={createdFrom} onChange={e => setCreatedFrom(e.target.value)} className={inputClasses} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Au</label>
              <input type="date" value={createdTo} onChange={e => setCreatedTo(e.target.value)} className={inputClasses} />
            </div>
          </div>
        );

      case 'birth':
        return (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Du</label>
              <input type="date" value={birthFrom} onChange={e => setBirthFrom(e.target.value)} className={inputClasses} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Au</label>
              <input type="date" value={birthTo} onChange={e => setBirthTo(e.target.value)} className={inputClasses} />
            </div>
          </div>
        );

      case 'company':
        return (
          <select value={companyId} onChange={e => setCompanyId(e.target.value)} className={inputClasses}>
            <option value="">Toutes les entreprises</option>
            {companies?.data?.map((c: { id: number; name: string }) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        );

      case 'fondation':
        return (
          <select
            value={entityType === 'customers' ? fondation : pfFondation}
            onChange={e => entityType === 'customers' ? setFondation(e.target.value) : setPfFondation(e.target.value)}
            className={inputClasses}
          >
            <option value="">Toutes les fondations</option>
            {fondations?.data?.map((f: { id: number; name: string }) => (
              <option key={f.id} value={f.name}>{f.name}</option>
            ))}
          </select>
        );

      case 'caisse':
        return (
          <select value={caisse} onChange={e => setCaisse(e.target.value)} className={inputClasses}>
            <option value="">Toutes les caisses</option>
            {caisses?.data?.map((c: { id: number; caisse: string }) => (
              <option key={c.id} value={c.caisse}>{c.caisse}</option>
            ))}
          </select>
        );

      case 'sit_pro':
        return (
          <select value={sitPro} onChange={e => setSitPro(e.target.value)} className={inputClasses}>
            <option value="">Toutes les situations</option>
            <option value="Employé">Employé</option>
            <option value="Demandeur d'emploi">Demandeur d&apos;emploi</option>
            <option value="Retraité">Retraité</option>
            <option value="Indépendant">Indépendant</option>
            <option value="Indépendant affilié">Indépendant affilié à une caisse de pension</option>
            <option value="Indépendant non affilié">Indépendant non affilié à un fond de pension</option>
          </select>
        );

      case 'recherche':
        return (
          <div className="flex gap-2">
            {[{ v: '1', l: 'Oui' }, { v: '0', l: 'Non' }].map(opt => (
              <button key={opt.v} type="button" onClick={() => setRecherche(recherche === opt.v ? '' : opt.v)}
                className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors border ${
                  recherche === opt.v ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                }`}
              >{opt.l}</button>
            ))}
          </div>
        );

      case 'regroupement':
        return (
          <div className="flex gap-2">
            {[{ v: '1', l: 'Oui' }, { v: '0', l: 'Non' }].map(opt => (
              <button key={opt.v} type="button" onClick={() => setRegroupement(regroupement === opt.v ? '' : opt.v)}
                className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors border ${
                  regroupement === opt.v ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                }`}
              >{opt.l}</button>
            ))}
          </div>
        );

      case 'pf_company':
        return (
          <select value={pfCompanyId} onChange={e => setPfCompanyId(e.target.value)} className={inputClasses}>
            <option value="">Toutes les entreprises</option>
            {companies?.data?.map((c: { id: number; name: string }) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        );

      case 'amount':
        return (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Min (CHF)</label>
              <input type="number" value={minAmount} onChange={e => setMinAmount(e.target.value)} placeholder="0" className={inputClasses} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max (CHF)</label>
              <input type="number" value={maxAmount} onChange={e => setMaxAmount(e.target.value)} placeholder="999 999" className={inputClasses} />
            </div>
          </div>
        );

      case 'sending_date':
        return (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Du</label>
              <input type="date" value={sendingDateFrom} onChange={e => setSendingDateFrom(e.target.value)} className={inputClasses} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Au</label>
              <input type="date" value={sendingDateTo} onChange={e => setSendingDateTo(e.target.value)} className={inputClasses} />
            </div>
          </div>
        );

      case 'reception_date':
        return (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Du</label>
              <input type="date" value={receptionDateFrom} onChange={e => setReceptionDateFrom(e.target.value)} className={inputClasses} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Au</label>
              <input type="date" value={receptionDateTo} onChange={e => setReceptionDateTo(e.target.value)} className={inputClasses} />
            </div>
          </div>
        );

      case 'commission_date':
        return (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Du</label>
              <input type="date" value={commissionDateFrom} onChange={e => setCommissionDateFrom(e.target.value)} className={inputClasses} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Au</label>
              <input type="date" value={commissionDateTo} onChange={e => setCommissionDateTo(e.target.value)} className={inputClasses} />
            </div>
          </div>
        );

      case 'commissioned':
        return (
          <div className="flex gap-2">
            {[{ v: '1', l: 'Oui' }, { v: '0', l: 'Non' }].map(opt => (
              <button key={opt.v} type="button" onClick={() => setCommissioned(commissioned === opt.v ? '' : opt.v)}
                className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors border ${
                  commissioned === opt.v ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                }`}
              >{opt.l}</button>
            ))}
          </div>
        );

      default: return null;
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Exports</h1>
        <p className="text-gray-600 mt-1">Sélectionnez vos filtres et exportez en Excel</p>
      </div>

      {/* Step 1: Entity type */}
      <div className="bg-white rounded-lg shadow p-5 mb-4">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Type de données</h2>
        <div className="grid grid-cols-2 gap-4">
          <button type="button" onClick={() => handleEntityChange('customers')}
            className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-colors ${
              entityType === 'customers'
                ? 'border-primary-600 bg-primary-50'
                : 'border-gray-200 bg-white hover:bg-gray-50'
            }`}
          >
            <div className={`p-3 rounded-lg ${entityType === 'customers' ? 'bg-primary-100' : 'bg-gray-100'}`}>
              <Users className={`h-6 w-6 ${entityType === 'customers' ? 'text-primary-600' : 'text-gray-400'}`} />
            </div>
            <div className="text-left">
              <div className={`font-semibold ${entityType === 'customers' ? 'text-primary-700' : 'text-gray-700'}`}>Clients</div>
              <div className="text-xs text-gray-500">Données personnelles et montants</div>
            </div>
          </button>

          <button type="button" onClick={() => handleEntityChange('pension-funds')}
            className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-colors ${
              entityType === 'pension-funds'
                ? 'border-primary-600 bg-primary-50'
                : 'border-gray-200 bg-white hover:bg-gray-50'
            }`}
          >
            <div className={`p-3 rounded-lg ${entityType === 'pension-funds' ? 'bg-primary-100' : 'bg-gray-100'}`}>
              <Building2 className={`h-6 w-6 ${entityType === 'pension-funds' ? 'text-primary-600' : 'text-gray-400'}`} />
            </div>
            <div className="text-left">
              <div className={`font-semibold ${entityType === 'pension-funds' ? 'text-primary-700' : 'text-gray-700'}`}>Caisses de pension</div>
              <div className="text-xs text-gray-500">Montants, statuts et commissions</div>
            </div>
          </button>
        </div>
      </div>

      {/* Presets */}
      {presets?.data && presets.data.length > 0 && (
        <div className="bg-white rounded-lg shadow p-5 mb-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Presets sauvegardés</h2>
          <div className="flex flex-wrap gap-2">
            {presets.data.map((preset: { id: number; name: string; entity_type: string; filters: Record<string, unknown>; columns: string[] | null }) => (
              <div key={preset.id} className="flex items-center gap-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                <button type="button" onClick={() => loadPreset(preset)}
                  className="text-sm font-medium text-gray-700 hover:text-primary-600 transition-colors"
                >
                  {preset.name}
                </button>
                <button type="button" onClick={() => deletePreset(preset.id)}
                  className="p-0.5 text-gray-300 hover:text-red-500 transition-colors ml-1"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Step 2: Filters */}
      <div className="bg-white rounded-lg shadow p-5 mb-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Filtres
            <span className="text-sm font-normal text-gray-400 ml-2">(optionnel)</span>
          </h2>
          <div className="flex items-center gap-3">
            {activeFilters.size > 0 && (
              <>
                <button type="button" onClick={() => setShowSavePreset(true)} className="flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 transition-colors">
                  <Save className="w-3.5 h-3.5" />
                  Sauvegarder
                </button>
                <button type="button" onClick={resetAll} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-red-600 transition-colors">
                  <RotateCcw className="w-3.5 h-3.5" />
                  Tout effacer
                </button>
              </>
            )}
          </div>
        </div>

        {/* Save preset inline form */}
        {showSavePreset && (
          <div className="flex items-center gap-2 mb-4 p-3 bg-primary-50 border border-primary-200 rounded-lg">
            <input
              type="text"
              value={presetName}
              onChange={e => setPresetName(e.target.value)}
              placeholder="Nom du preset..."
              className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
              onKeyDown={e => e.key === 'Enter' && savePreset()}
              autoFocus
            />
            <button type="button" onClick={savePreset} disabled={isSavingPreset || !presetName.trim()}
              className="px-3 py-1.5 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50 transition-colors"
            >
              {isSavingPreset ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Sauvegarder'}
            </button>
            <button type="button" onClick={() => { setShowSavePreset(false); setPresetName(''); }}
              className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Active filters */}
        {activeFilters.size > 0 && (
          <div className="space-y-3 mb-4">
            {currentFilters.filter(f => activeFilters.has(f.key)).map(filter => (
              <div key={filter.key} className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-b border-gray-200">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400">{filter.icon}</span>
                    <span className="text-sm font-medium text-gray-700">{filter.label}</span>
                  </div>
                  <button type="button" onClick={() => removeFilter(filter.key)}
                    className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="p-4">
                  {renderFilterContent(filter.key)}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Available filters */}
        {inactiveFilters.length > 0 && (
          <div className="border border-dashed border-gray-300 rounded-lg p-4">
            <p className="text-xs text-gray-500 mb-3">Cliquez pour ajouter un filtre</p>
            <div className="flex flex-wrap gap-2">
              {inactiveFilters.map(filter => (
                <button key={filter.key} type="button" onClick={() => toggleFilter(filter.key)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-gray-200 bg-white hover:border-primary-300 hover:bg-primary-50 text-gray-600 hover:text-primary-700 transition-colors text-sm"
                >
                  <Plus className="w-3.5 h-3.5" />
                  {filter.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Step 3: Export */}
      <div className="bg-white rounded-lg shadow p-5">
        <button type="button" onClick={openColumnModal} disabled={isExporting}
          className="w-full px-5 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm flex items-center justify-center gap-3 font-medium"
        >
          {isExporting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Génération en cours...</span>
            </>
          ) : (
            <>
              <Download className="w-5 h-5" />
              <span>Télécharger l&apos;export Excel</span>
              {activeFilters.size > 0 && (
                <span className="bg-white/20 text-xs px-2 py-0.5 rounded-full">{activeFilters.size} filtre{activeFilters.size > 1 ? 's' : ''}</span>
              )}
              <ChevronRight className="w-4 h-4 opacity-50" />
            </>
          )}
        </button>
      </div>

      {/* Column picker modal */}
      {showColumnModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowColumnModal(false)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Colonnes à exporter</h3>
              <button type="button" onClick={() => setShowColumnModal(false)} className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-3 border-b border-gray-100">
              <button type="button" onClick={toggleAllColumns}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
              >
                {selectedColumns.size === Object.keys(currentColumns).length ? 'Tout désélectionner' : 'Tout sélectionner'}
              </button>
              <span className="text-sm text-gray-400 ml-3">
                {selectedColumns.size}/{Object.keys(currentColumns).length} colonnes
              </span>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-3">
              <div className="space-y-1">
                {Object.entries(currentColumns).map(([key, label]) => (
                  <button key={key} type="button" onClick={() => toggleColumn(key)}
                    className="flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors w-full text-left">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors flex-shrink-0 ${
                      selectedColumns.has(key) ? 'bg-primary-600 border-primary-600' : 'border-gray-300'
                    }`}>
                      {selectedColumns.has(key) && <Check className="w-3.5 h-3.5 text-white" />}
                    </div>
                    <span className="text-sm text-gray-700">{label}</span>
                    <span className="text-xs text-gray-400 ml-auto">{key}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
              <button type="button" onClick={() => setShowColumnModal(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
              <button type="button" onClick={handleExport} disabled={selectedColumns.size === 0}
                className="px-5 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50 transition-colors flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Exporter ({selectedColumns.size} colonnes)
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExportPage;
